import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    IconButton,
    Snackbar,
    Alert,
    styled,
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Schedule as ScheduleIcon,
} from "@mui/icons-material";
import MarkdownEditor from "../components/MarketingMessages/MarkdownEditor";
import PublishMessageModal from "../components/MarketingMessages/PublishMessageModal";
import MessagePreview from "../components/MarketingMessages/MessagePreview";
import {
    applyMessageRecordToForm,
    buildSaveInfoMessagePayload,
    normalizePopupDataObj,
    normalizeTargetPublishTime,
    resolveTargetPublishTime,
} from "../components/MarketingMessages/marketingApi";
import { useAxiosWithAuth } from "../common/api/hooks/useAxiosWithAuth";
import { URL } from "../common/api/urls";
import { TEXT_MSGS } from "../common/utility/constant";
import { useNavigate, useParams, useLocation } from "react-router-dom";


const RootContainer = styled(Box)({
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#081329",
    color: "#ffffff",
    padding: "24px 32px",
    boxSizing: "border-box",
});

const StatusChip = styled(Chip)(({ state }) => {
    const map = {
        Draft: { bg: "rgba(255, 193, 7, 0.15)", color: "#ffc107" },
        Queued: { bg: "rgba(63, 105, 248, 0.2)", color: "#7a9cff" },
        Scheduled: { bg: "rgba(63, 105, 248, 0.2)", color: "#7a9cff" },
        Delivered: { bg: "rgba(40, 167, 69, 0.2)", color: "#28a745" },
        Cancelled: { bg: "rgba(244, 67, 54, 0.15)", color: "#ff6b6b" },
        InProcess: {
            bg: "rgba(255, 152, 0, 0.15)",
            color: "#ff9800",
        },
    };
    const c = map[state] || { bg: "rgba(255,255,255,0.1)", color: "#fff" };
    return {
        backgroundColor: c.bg,
        color: c.color,
        fontWeight: 600,
        fontSize: "0.75rem",
        borderRadius: "4px",
        height: "24px",
    };
});

const DEFAULT_CONTENT = `-- *sample content* --

## Header goes here

some message

- List item 1
- List item 2

> closing comments`;

const VIEW_TOGGLE_OPTIONS = [
    { key: "edit", label: "Editor Only" },
    { key: "split", label: "Split View" },
    { key: "preview", label: "Preview Only" },
];

const CANCEL_BUTTON_LABEL = "Cancel";

function arraysEqual(a, b) {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
}

function snapshotFromForm(form) {
    return {
        title: (form.title ?? "").trim(),
        content: form.content ?? "",
        popupConfig: {
            type: form.popupConfig?.type ?? "info",
            action: form.popupConfig?.action?.trim() ?? "",
        },
        status: form.status ?? "Draft",
        userAccessKeyList: form.userAccessKeyList ?? [],
        targetPublishTime: normalizeTargetPublishTime(form.targetPublishTime),
    };
}

function buildPartialEditPayload({
    id,
    snapshot,
    popupDataObj,
    userAccessKeyList,
    targetPublishTime,
    saveKind,
}) {
    const popup = normalizePopupDataObj(popupDataObj);
    const payload = { id };

    const popupChanged =
        popup.title !== snapshot.title ||
        popup.msg !== snapshot.content ||
        popup.type !== snapshot.popupConfig.type ||
        popup.action !== snapshot.popupConfig.action;

    if (popupChanged) {
        payload.popup_data_obj = popup;
    }

    if (saveKind === "publish") {
        payload.status = "queued";
        if (!arraysEqual(userAccessKeyList, snapshot.userAccessKeyList)) {
            payload.user_access_key_list = userAccessKeyList;
        }
        const nextTime = normalizeTargetPublishTime(targetPublishTime);
        if (nextTime !== snapshot.targetPublishTime) {
            payload.target_publish_time = targetPublishTime;
        }
    }

    if (saveKind === "draft") {
        payload.status = "draft";
    }

    return payload;
}

export default function MarketingMessagePage() {
    const navigate = useNavigate();
    const { messageId } = useParams();
    const location = useLocation();
    const isEditMode = Boolean(messageId);

    const [messageRecordId, setMessageRecordId] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [status, setStatus] = useState("Draft");
    const [title, setTitle] = useState("");
    const [content, setContent] = useState(DEFAULT_CONTENT);
    const [isDirty, setIsDirty] = useState(false);
    const [viewMode, setViewMode] = useState("split"); // 'edit' | 'preview' | 'split'
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [popupConfig, setPopupConfig] = useState({
        type: "info",
        action: "",
    });
    const [saving, setSaving] = useState(false);
    const [pendingSaveMeta, setPendingSaveMeta] = useState(null);
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const [, saveResponse, saveError, saveLoaded, resetSave, executeSaveAPI] =
        useAxiosWithAuth();
    const [, getResponse, getError, getLoaded, resetGet, executeGetAPI] =
        useAxiosWithAuth();

    const hydrateForm = (record) => {
        const form = applyMessageRecordToForm(record);
        setMessageRecordId(form.id);
        setTitle(form.title);
        setContent(form.content || DEFAULT_CONTENT);
        setPopupConfig(form.popupConfig);
        setStatus(form.status);
        setSavedSnapshot(
            snapshotFromForm({
                ...form,
                content: form.content || DEFAULT_CONTENT,
            }),
        );
        setIsDirty(false);
    };

    const refreshSavedSnapshot = (overrides = {}) => {
        setSavedSnapshot(
            snapshotFromForm({
                title,
                content,
                popupConfig,
                status: overrides.status ?? status,
                userAccessKeyList:
                    overrides.userAccessKeyList ??
                    savedSnapshot?.userAccessKeyList ??
                    [],
                targetPublishTime:
                    overrides.targetPublishTime ??
                    savedSnapshot?.targetPublishTime ??
                    0,
            }),
        );
    };

    useEffect(() => {
        if (!isEditMode) return;

        const stateMessage = location.state?.message;
        if (stateMessage && String(stateMessage.id) === String(messageId)) {
            hydrateForm(stateMessage);
            setLoadingMessage(false);
        } else {
            setLoadingMessage(true);
        }
        setLoadError(null);
        executeGetAPI(URL.INFO_MESSAGE_DETAIL(messageId), "GET");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageId, isEditMode]);

    useEffect(() => {
        if (!getLoaded || !isEditMode) return;

        setLoadingMessage(false);

        if (getResponse != null) {
            const record = getResponse?.result ?? getResponse?.data ?? getResponse;
            if (record && typeof record === "object") {
                hydrateForm(record);
            }
            resetGet();
            return;
        }

        if (getError != null) {
            if (!location.state?.message) {
                setLoadError(
                    getError?.response?.data?.message ??
                        TEXT_MSGS.NETWORK_ERROR_MSG,
                );
            }
            resetGet();
        }
    }, [getLoaded, getResponse, getError, isEditMode, resetGet, location.state]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    const updateTitle = (v) => {
        setTitle(v);
        setIsDirty(true);
    };

    const updateContent = (v) => {
        setContent(v);
        setIsDirty(true);
    };

    const buildPopupDataObj = () => ({
        title: title.trim(),
        msg: content,
        type: popupConfig.type,
        action: popupConfig.action?.trim() ?? "",
    });

    const postInfoMessage = async ({
        userAccessKeyList,
        targetPublishTime,
        saveKind,
    }) => {
        const popupDataObj = buildPopupDataObj();
        let payload;

        if (messageRecordId && savedSnapshot) {
            payload = buildPartialEditPayload({
                id: messageRecordId,
                snapshot: savedSnapshot,
                popupDataObj,
                userAccessKeyList,
                targetPublishTime,
                saveKind,
            });

            if (Object.keys(payload).length === 1) {
                setToast({
                    open: true,
                    message: "No changes to save",
                    severity: "info",
                });
                return;
            }
            else {
                setToast({
                    open: true,
                    message:
                        saveResponse?.message ??
                        "Changes saved successfully",
                    severity: "success",
                });
            }
        } else {
            payload = buildSaveInfoMessagePayload({
                userAccessKeyList,
                popupDataObj,
                targetPublishTime,
                status: saveKind === "publish" ? "queued" : "draft",
            });
        }

        setSaving(true);
        await executeSaveAPI(URL.SAVE_INFO_MESSAGE, "POST", payload);
    };

    useEffect(() => {
        if (!saveLoaded) return;

        setSaving(false);

        if (saveResponse != null) {
            setIsDirty(false);

            const savedId =
                saveResponse?.result?.id ??
                saveResponse?.id ??
                saveResponse?.data?.id;
            if (savedId != null) {
                setMessageRecordId(savedId);
                if (!isEditMode) {
                    navigate(`/marketing-messages/${savedId}`, { replace: true });
                }
            }

            if (pendingSaveMeta?.kind === "publish") {
                const {
                    publishOption,
                    scheduleDate,
                    scheduleTime,
                    targetPlans,
                    targetPublishTime,
                } = pendingSaveMeta;
                const audience =
                    targetPlans?.length ? ` (${targetPlans.join(", ")})` : "";

                setStatus("Queued");
                refreshSavedSnapshot({
                    status: "Queued",
                    userAccessKeyList: targetPlans,
                    targetPublishTime,
                });

                if (publishOption === "later" || publishOption === "5min") {
                    const isInsight = popupConfig?.type === "insight";
                    setToast({
                        open: true,
                        message: isInsight
                            ? `Insight scheduled to be shown until ${scheduleDate} at ${scheduleTime}${audience}`
                            : `Message scheduled for ${
                                publishOption === "5min"
                                    ? "5 minutes from now"
                                    : `${scheduleDate} at ${scheduleTime}`
                            }${audience}`,
                        severity: "info",
                    });
                } else {
                    setToast({
                        open: true,
                        message: `Marketing message delivered live!${audience}`,
                        severity: "success",
                    });
                }
            } else if (pendingSaveMeta?.kind === "draft") {
                setStatus("Draft");
                refreshSavedSnapshot({ status: "Draft" });
                setToast({
                    open: true,
                    message:
                        saveResponse?.message ??
                        "Draft saved successfully",
                    severity: "success",
                });
            }
            else if (pendingSaveMeta?.kind === "cancel") {
    setStatus("Cancelled");
    setIsDirty(false);

    refreshSavedSnapshot({
        status: "Cancelled",
    });

    setToast({
        open: true,
        message:
            saveResponse?.message ??
            "Marketing message cancelled successfully",
        severity: "info",
    });
}
            setPendingSaveMeta(null);
            resetSave();
            return;
        }


        if (saveError != null) {
            setPendingSaveMeta(null);
            setToast({
                open: true,
                message:
                    saveError?.response?.data?.message ??
                    TEXT_MSGS.NETWORK_ERROR_MSG,
                severity: "error",
            });
            resetSave();
        }
    }, [saveLoaded, saveResponse, saveError, resetSave, pendingSaveMeta]);

    const handleSaveDraft = () => {
        setPendingSaveMeta({ kind: "draft" });
        postInfoMessage({
            userAccessKeyList: savedSnapshot?.userAccessKeyList ?? [],
            targetPublishTime: savedSnapshot?.targetPublishTime ?? 0,
            saveKind: "draft",
        });
    };

    const handleUpdate = () => {
        setPendingSaveMeta({ kind: "queued" });
        postInfoMessage({
            userAccessKeyList: savedSnapshot?.userAccessKeyList ?? [],
            targetPublishTime: savedSnapshot?.targetPublishTime ?? 0,
            saveKind: savedSnapshot?.targetPublishTime == 0 ? "draft" : "queued",
        });
    };

const handleCancelMessage = () => {
    if (saving) return;

    if (!messageRecordId) {
        navigate("/marketing-messages");
        return;
    }

    setPendingSaveMeta({ kind: "cancel" });
    setSaving(true);

    executeSaveAPI(URL.SAVE_INFO_MESSAGE, "POST", {
        id: messageRecordId,
        status: "cancelled",
    });
};
    const handleConfirmPublish = ({
        publishOption,
        scheduleDate,
        scheduleTime,
        targetPlans,
    }) => {
        const targetPublishTime = resolveTargetPublishTime({
            publishOption,
            scheduleDate,
            scheduleTime,
        });

        setPendingSaveMeta({
            kind: "publish",
            publishOption,
            scheduleDate,
            scheduleTime,
            targetPlans,
            targetPublishTime,
        });

        postInfoMessage({
            userAccessKeyList: targetPlans,
            targetPublishTime,
            saveKind: "publish",
        });

        setPublishModalOpen(false);
        navigate("/marketing-messages", { replace: true });
        setTimeout(() => {
            navigate(0);
        }, 0);
    };

    if (isEditMode && loadingMessage) {
        return (
            <RootContainer>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    Loading message…
                </Typography>
            </RootContainer>
        );
    }

    if (isEditMode && loadError) {
        return (
            <RootContainer>
                <Typography variant="body1" sx={{ color: "#ff6b6b", mb: 2 }}>
                    {loadError}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => navigate("/marketing-messages")}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)" }}
                >
                    Back to list
                </Button>
            </RootContainer>
        );
    }

    return (
        <RootContainer>
            {/* HEADER */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <IconButton
                        onClick={() => navigate("/marketing-messages")}
                        sx={{ color: "#b0c4de" }}
                        aria-label="Back to messages list"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#fff" }}
                    >
                        {isEditMode
                            ? "Edit Marketing Message"
                            : "Create Marketing Message"}
                    </Typography>
                    <StatusChip label={status} state={status} />
                    {isDirty && (
                        <Typography
                            variant="caption"
                            sx={{ color: "#ffc107", fontStyle: "italic" }}
                        >
                            • Unsaved changes
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 1.5 }}>
                    {/* <Button
                        variant="outlined"
                        onClick={handleCancelMessage}
                        disabled={saving}
                        sx={{
                            color: "#ff6b6b",
                            borderColor: "rgba(255, 107, 107, 0.5)",
                            textTransform: "none",
                            "&:hover": {
                                borderColor: "#ff6b6b",
                                backgroundColor: "rgba(255, 107, 107, 0.08)",
                            },
                        }}
                    >
                        {isEditMode ? "Cancel Message" : CANCEL_BUTTON_LABEL}
                    </Button> */}

                    {status !== "Delivered" && status !== "Cancelled" && status !== "Queued" && (
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveDraft}
                            disabled={saving}
                            sx={{
                                color: "#ffffff",
                                borderColor: "rgba(255, 255, 255, 0.3)",
                                textTransform: "none",
                                "&:hover": {
                                    borderColor: "#ffffff",
                                    backgroundColor:
                                        "rgba(255, 255, 255, 0.05)",
                                },
                            }}
                        >
                            Save Draft
                        </Button>
                    )}
                    {status == "Queued" && (
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleUpdate}
                            disabled={saving}
                            sx={{
                                color: "#ffffff",
                                borderColor: "rgba(255, 255, 255, 0.3)",
                                textTransform: "none",
                                "&:hover": {
                                    borderColor: "#ffffff",
                                    backgroundColor:
                                        "rgba(255, 255, 255, 0.05)",
                                },
                            }}
                        >
                            Update
                        </Button>
                    )}

                    {status !== "Cancelled" && (
                        <Button
                            variant="contained"
                            startIcon={
                                status === "Scheduled" ? (
                                    <ScheduleIcon />
                                ) : (
                                    <SendIcon />
                                )
                            }
                            onClick={() => setPublishModalOpen(true)}
                            disabled={saving}
                            sx={{
                                backgroundColor: "#3f69f8",
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": {
                                    backgroundColor: "#2b52d9",
                                },
                            }}
                        >
                            {status === "Delivered"
                                ? "Update Message"
                                : status === "Scheduled"
                                ? "Reschedule"
                                : "Save & Publish"}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* MESSAGE CONFIGURATION */}
            <Box
                sx={{
                    backgroundColor: "#0d2147",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "10px",
                    p: 3,
                    mb: 3,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    alignItems: "flex-start",
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        width: "100%",
                        color: "#7a9cff",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontWeight: 700,
                    }}
                >
                    Popup Configuration
                </Typography>

                <TextField
                    label="Message Title / Campaign Name"
                    value={title}
                    onChange={(e) => updateTitle(e.target.value)}
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                    helperText={`${title.length}/100 characters`}
                    FormHelperTextProps={{
                        sx: {
                            color: "rgba(255, 255, 255, 0.5)",
                            textAlign: "right",
                        },
                    }}
                    sx={{
                        flex: "1 1 320px",
                        "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            backgroundColor: "#091730",
                            "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.15)",
                            },
                            "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "#3f69f8",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: "rgba(255, 255, 255, 0.7)",
                        },
                    }}
                />

                <Box
                    sx={{
                        backgroundColor: "#091730",
                        p: 0.5,
                        borderRadius: "6px",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        display: "flex",
                    }}
                >
                    {/* {VIEW_TOGGLE_OPTIONS.map((opt) => (
                        <Button
                            key={opt.key}
                            size="small"
                            onClick={() => setViewMode(opt.key)}
                            sx={{
                                textTransform: "none",
                                color:
                                    viewMode === opt.key ? "#fff" : "#b0c4de",
                                backgroundColor:
                                    viewMode === opt.key
                                        ? "#3f69f8"
                                        : "transparent",
                            }}
                        >
                            {opt.label}
                        </Button>
                    ))} */}
                </Box>
            </Box>

            {/* EDITOR + PREVIEW — plain flexbox, fills 100% of whatever width the parent gives it.
          No MUI Grid here on purpose: Grid's negative-margin gutter trick is what was causing
          the layout drift in your screenshot. See chat notes for the actual root cause. */}
            <Box
                sx={{
                    display: "flex",
                    gap: 3,
                    width: "100%",
                    flexWrap: { xs: "wrap", lg: "nowrap" },
                }}
            >
                {(viewMode === "edit" || viewMode === "split") && (
                    <Box
                        sx={{
                            flex: viewMode === "split" ? "1 1 50%" : "1 1 100%",
                            minWidth: 0,
                        }}
                    >
                        <MarkdownEditor
                            value={content}
                            onChange={updateContent}
                        />
                    </Box>
                )}
                {(viewMode === "preview" || viewMode === "split") && (
                    <Box
                        sx={{
                            flex: viewMode === "split" ? "1 1 50%" : "1 1 100%",
                            minWidth: 0,
                        }}
                    >
                        <MessagePreview
                            content={content}
                            popupTitle={title}
                            popupConfig={popupConfig}
                            onPopupConfigChange={(next) => {
                                setPopupConfig(next);
                                setIsDirty(true);
                            }}
                        />
                    </Box>
                )}
            </Box>

            <PublishMessageModal
                open={publishModalOpen}
                onClose={() => setPublishModalOpen(false)}
                onConfirm={handleConfirmPublish}
                popup_message_type={popupConfig?.type}
                popupMessageType={popupConfig?.type}
            />

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={toast.severity}
                    sx={{
                        backgroundColor: "#0d2147",
                        color: "#fff",
                        border: "1px solid #1e3a70",
                    }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </RootContainer>
    );
}
